import { defineStore } from 'pinia'
import {
  applicationStates as _applicationStates,
  overrideMarketplace as _overrideMarketplace,
  overrideOptions as _overrideOptions
} from '~/libs/user-override'

import ApprovalsMock from '~/cypress/fixtures/approvalsMock.json'

import { localStorageService } from '~/libs/local-storage.service'
import { USER_VIEW_CONSTANTS } from '~/libs/user-override'

import { testDealWithOffer } from '~/libs/user-override'

const overrideItems = [
  'currentViewState',
  'currentAppState',
  'currentDealState',
  'currentScoreValues',
  'dealList',
  'approvalAmount',
  'approvalRenewal',
  'adjustmentCalculatorDarkLaunched',
  'offerStipulationsOption',
  'stipulationExperiment'
]

const checkOverrideItem = function (setOverrideItem, key) {
  const overrideItem = localStorageService.getItem(key)
  if (overrideItem) {
    setOverrideItem(key, overrideItem)
  }
}

export const useUserOverrideStore = defineStore('userOverride', () => {

/*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/
  const overrideModalOpen = ref(false)
  const overrideOptions = ref(_overrideOptions)
  const overrideMarketplace = ref(_overrideMarketplace)
  const applicationStates = ref(_applicationStates)
  const currentViewState = ref(null)
  const currentAppState = ref(null)
  const currentDealState = ref(null)
  const currentScoreValues = ref({})
  const dealList = ref([])
  const approvalAmount = ref(null)
  const approvalRenewal = ref('None')
  const showLenderReviews = ref(false)
  const adjustmentCalculatorDarkLaunched = ref(false)
  const offerStipulationsOption = ref('created')
  const stipulationExperiment = ref(false)

  const _state = {
    overrideModalOpen,
    overrideOptions,
    overrideMarketplace,
    applicationStates,
    currentViewState,
    currentAppState,
    currentDealState,
    currentScoreValues,
    dealList,
    approvalAmount,
    approvalRenewal,
    showLenderReviews,
    adjustmentCalculatorDarkLaunched,
    offerStipulationsOption,
    stipulationExperiment,
  }
  // Copy _state at initialization
  const _defaultState = Object.entries(_state)
    .reduce((acc, [k, v]) => { acc[k] = v.value; return acc }, {})

/*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/
  const isQualifiedViewState = computed(() => {
    return currentViewState.value === USER_VIEW_CONSTANTS.QUALIFIED
  })
  const isUnqualifiedViewState = computed(() => {
    return currentViewState.value === USER_VIEW_CONSTANTS.UNQUALIFIED
  })
  // Check localStorage to ensure we can load user override on new page loads.
  const getOverrideViewState = computed(() => {
    if (!currentViewState.value) {
      if (process.client) {
        // It's a string so we don't need to JSON.parse it.
        const _currentViewState =
          localStorageService.getItem('currentViewState')
        if (_currentViewState) {
          return _currentViewState
        }
      }
    }
    return currentViewState.value
  })
  const getOverrideAppState = computed(() => {
    return currentAppState.value
  })
  const getOverrideDealState = computed(() => {
    return currentDealState.value
  })
  const getOverrideScoreValues = computed(() => {
    return currentScoreValues.value
  })
  const getOverrideDealList = computed(() => {
    if (!dealList.value.length) {
      // Check localStorage to recover state from page reloads.
      if (process.client) {
        const _dealList = localStorageService.getItem('dealList')
        return _dealList ? JSON.parse(_dealList) : null
      }
    }
    // Return default value.
    return dealList.value
  })
  const hasApprovalsOverride = computed(() => {
    return approvalAmount.value !== null
  })
  const getApprovalsMock = computed(() => {
    let mocks = ApprovalsMock.data

    // If we're doing a renewal, set flag to true and provide override points
    // from last funded deal. This is a quick hack for testing On Deck ACH
    // renewals. As needed we'll want to make it more robust.
    if (approvalRenewal.value !== 'None') {
      mocks[0].isSupportedRenewal = true
      mocks[0].lastAcceptedOfferPoints = 8
    }
    return approvalAmount.value
      ? mocks.slice(0, approvalAmount.value)
      : []
  })
  const showAdjustmentCalculatorOverride = computed(() => {
    return adjustmentCalculatorDarkLaunched.value
  })

  const getOfferStipulationsOption = computed(() => {
    return offerStipulationsOption.value
  })

  const getStipulationExperiment = computed(() => {
    return stipulationExperiment.value
  })

/*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/
  function clearAllOverrides() {
    overrideItems.forEach((key) => {
      _state[key].value = _defaultState[key]
      localStorageService.removeItem(key)
    })
  }

  function setOverrideItem(key, val) {
    switch (key) {
      case 'currentScoreValues':
        this.setCurrentScoreValues(val)
      case 'dealList':
        this.setDealList(val)
        break
      default:
        _state[key].value = val
        localStorageService.setItem(key, val)
    }
  }

  function initialOverrideSetup() {
    overrideItems.forEach((key) => checkOverrideItem(this.setOverrideItem, key))
  }

  function setCurrentScoreValues(values) {
    currentScoreValues.value = { ...currentScoreValues.value, ...values }
    localStorageService.setItem(
      'currentScoreValues',
      JSON.stringify(currentScoreValues.value)
    )
  }

  function setDealList(dealObj) {
    const { stage, status } = dealObj
    const dealCopy = testDealWithOffer({ stage, status })
    const stateCopy =
      dealList.value.length > 0
        ? JSON.parse(JSON.stringify(dealList.value))
        : []
    stateCopy.push(dealCopy)
    dealList.value = stateCopy
    localStorageService.setItem(
      'overrideDealList',
      JSON.stringify(dealList.value)
    )
  }


  function setCurrentViewState(type) {
    currentViewState.value = type
    localStorageService.setItem('currentViewState', currentViewState.value)
  }

  function clearCurrentViewState() {
    currentViewState.value = null
    localStorageService.removeItem('currentViewState')
  }

  function setCurrentAppState(type) {
    currentAppState.value = type
    localStorageService.setItem('currentAppState', currentAppState.value)
  }

  function clearCurrentAppState() {
    currentAppState.value = null
    localStorageService.removeItem('currentAppState')
  }

  function setCurrentDealState(type) {
    currentDealState.value = type
    localStorageService.setItem('currentDealState', currentDealState.value)
  }

  function clearCurrentDealState() {
    currentDealState.value = null
    localStorageService.removeItem('currentDealState')
  }

  function clearCurrentScoreValues() {
    currentScoreValues.value = null
    localStorageService.removeItem('currentScoreValues')
  }

  function setApprovalAmount(val) {
    approvalAmount.value = val
    localStorageService.setItem('approvalAmount', approvalAmount.value)
  }

  function setApprovalRenewal(val) {
    approvalRenewal.value = val
    localStorageService.setItem('approvalRenewal', approvalRenewal.value)
  }

  function clearApprovalAmount() {
    approvalAmount.value = null
    localStorageService.removeItem('approvalAmount')
  }

  function clearApprovalRenewal() {
    approvalRenewal.value = null
    localStorageService.removeItem('approvalRenewal')
  }

  function clearAdjustmentCalculatorDarkLaunch() {
    approvalAmount.value = null
    localStorageService.removeItem('adjustmentCalculatorDarkLaunch')
  }

  function setLenderReviews(val) {
    showLenderReviews.value = val
  }

  function clearDealList() {
    dealList.value = []
    localStorageService.removeItem('overrideDealList')
  }

  function setAdjustmentCalculatorDarkLaunchState(val) {
    adjustmentCalculatorDarkLaunched.value = val
    if (val) {
      localStorageService.setItem(
        'adjustmentCalculatorDarkLaunch',
        adjustmentCalculatorDarkLaunched.value
      )
    } else {
      localStorageService.removeItem('adjustmentCalculatorDarkLaunch')
    }
  }

  function clearAdjustmentCalculatorDarkLaunchState() {
    adjustmentCalculatorDarkLaunched.value = null
    localStorageService.removeItem('adjustmentCalculatorDarkLaunch')
  }

  function setOfferStipulationsOption(option) {
    offerStipulationsOption.value = option
    localStorageService.setItem(
      'offerStipulationsOption',
      offerStipulationsOption.value
    )
  }

  function clearOfferStipulationsOption() {
    offerStipulationsOption.value = null
    localStorageService.removeItem('offerStipulationsOption')
  }

  function setStipulationExperiment(val) {
    stipulationExperiment.value = val
    localStorageService.setItem(
      'stipulationExperiment',
      stipulationExperiment.value
    )
  }

  function clearStipulationExperiment() {
    stipulationExperiment.value = null
    localStorageService.removeItem('stipulationExperiment')
  }

  return {
    // STATE
    overrideModalOpen,
    overrideOptions,
    overrideMarketplace,
    applicationStates,
    currentViewState,
    currentAppState,
    currentDealState,
    currentScoreValues,
    dealList,
    approvalAmount,
    approvalRenewal,
    showLenderReviews,
    adjustmentCalculatorDarkLaunched,
    offerStipulationsOption,
    stipulationExperiment,

    // GETTERS,
    isQualifiedViewState,
    isUnqualifiedViewState,
    getOverrideViewState,
    getOverrideAppState,
    getOverrideDealState,
    getOverrideScoreValues,
    getOverrideDealList,
    hasApprovalsOverride,
    getApprovalsMock,
    showAdjustmentCalculatorOverride,
    getOfferStipulationsOption,
    getStipulationExperiment,

    // ACTIONS,
    initialOverrideSetup,
    clearAllOverrides,
    setOverrideItem,
    setCurrentScoreValues,
    setDealList,
    setCurrentViewState,
    clearCurrentViewState,
    setCurrentAppState,
    clearCurrentAppState,
    setCurrentDealState,
    clearCurrentDealState,
    clearCurrentScoreValues,
    setApprovalAmount,
    setApprovalRenewal,
    clearApprovalAmount,
    clearApprovalRenewal,
    clearAdjustmentCalculatorDarkLaunch,
    setLenderReviews,
    clearDealList,
    setAdjustmentCalculatorDarkLaunchState,
    clearAdjustmentCalculatorDarkLaunchState,
    setOfferStipulationsOption,
    clearOfferStipulationsOption,
    setStipulationExperiment,
    clearStipulationExperiment,
  }
})
